// src/pages/trading-portal/symbol-detail/SymbolDetailPage.js
// TradingView-style symbol detail page with 11 modular tabs + watchlist side panel

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Tabs, Tab, CircularProgress, Alert, IconButton, Tooltip,
    Divider, Skeleton, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, InputAdornment, List, ListItem, ListItemText, ListItemSecondaryAction,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Dashboard as DashboardIcon,
    AccountBalance as AccountBalanceIcon,
    Newspaper as NewspaperIcon,
    Description as DescriptionIcon,
    Forum as ForumIcon,
    ShowChart as ShowChartIcon,
    TrendingUp as TrendingUpIcon,
    CalendarMonth as CalendarMonthIcon,
    CandlestickChart as CandlestickChartIcon,
    Refresh as RefreshIcon,
    FiberManualRecord as DotIcon,
    MenuOpen as MenuOpenIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

// Tab components
import OverviewTab from './tabs/OverviewTab';
import FinancialTab from './tabs/FinancialTab';
import NewsTab from './tabs/NewsTab';
import DocumentsTab from './tabs/DocumentsTab';
import CommunityTab from './tabs/CommunityTab';
import TechnicalsTab from './tabs/TechnicalsTab';
import ForecastsTab from './tabs/ForecastsTab';
import SeasonalsTab from './tabs/SeasonalsTab';
import OptionsTab from './tabs/OptionsTab';

const TAB_CONFIG = [
    { label: 'Overview', icon: <DashboardIcon />, endpoint: 'overview', Component: OverviewTab },
    { label: 'Financial', icon: <AccountBalanceIcon />, endpoint: 'financial', Component: FinancialTab },
    { label: 'News', icon: <NewspaperIcon />, endpoint: 'news', Component: NewsTab },
    { label: 'Documents', icon: <DescriptionIcon />, endpoint: 'documents', Component: DocumentsTab },
    { label: 'Community', icon: <ForumIcon />, endpoint: 'community', Component: CommunityTab },
    { label: 'Technicals', icon: <ShowChartIcon />, endpoint: 'technicals', Component: TechnicalsTab },
    { label: 'Forecasts', icon: <TrendingUpIcon />, endpoint: 'forecasts', Component: ForecastsTab },
    { label: 'Seasonals', icon: <CalendarMonthIcon />, endpoint: 'seasonals', Component: SeasonalsTab },
    { label: 'Options', icon: <CandlestickChartIcon />, endpoint: 'options', Component: OptionsTab },
];

// ── Watchlist Side Panel ──
const SYMBOL_COLORS = ['#2196f3', '#e91e63', '#ff9800', '#4caf50', '#9c27b0', '#00bcd4', '#f44336', '#3f51b5', '#009688', '#ff5722'];
const getSymbolColor = (symbol) => SYMBOL_COLORS[(symbol || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % SYMBOL_COLORS.length];

const WatchlistPanel = ({ symbols, loading, currentSymbolId, onSymbolClick, onRefresh }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [symbolSearch, setSymbolSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [addingSymbol, setAddingSymbol] = useState(null);
    const [deletingSymbol, setDeletingSymbol] = useState(null);

    const handleSymbolSearch = async () => {
        if (!symbolSearch.trim()) return;
        setSearching(true);
        try {
            const res = await axiosInstance.get(`/api/stock-trading/symbols/search?q=${encodeURIComponent(symbolSearch)}`);
            setSearchResults(res.data?.data || res.data?.results || []);
        } catch (e) { /* ignore */ }
        finally { setSearching(false); }
    };

    const handleAddSymbol = async (result) => {
        const ticker = result.symbol || result.ticker;
        setAddingSymbol(ticker);
        try {
            const res = await axiosInstance.post('/api/stock-trading/symbols', {
                symbol: ticker,
                company_name: result.name || result.company_name,
                market_type: result.market_type || result.exchange || 'OTHER',
            });
            if (res.data.success) {
                const newId = res.data.data?.id;
                if (newId) { try { await axiosInstance.post(`/api/stock-trading/symbols/${newId}/fetch-price`, { timeframe: 'DAILY', limit: 1 }); } catch (e) { /* ignore */ } }
                setSearchResults(prev => prev.filter(s => (s.symbol || s.ticker) !== ticker));
                onRefresh();
            }
        } catch (e) { /* ignore */ }
        finally { setAddingSymbol(null); }
    };

    const handleRemoveSymbol = async (e, symbolId) => {
        e.stopPropagation();
        setDeletingSymbol(symbolId);
        try {
            await axiosInstance.delete(`/api/stock-trading/symbols/${symbolId}`);
            onRefresh();
        } catch (e) { /* ignore */ }
        finally { setDeletingSymbol(null); }
    };

    const groupByType = (items) => {
        const groups = {};
        items.forEach(s => {
            const type = s.market_type || 'OTHER';
            if (!groups[type]) groups[type] = [];
            groups[type].push(s);
        });
        return groups;
    };

    const grouped = groupByType(symbols);
    const panelWidth = isCollapsed ? 56 : 320;

    return (
        <>
            <Paper sx={{
                width: panelWidth, flexShrink: 0, borderRadius: 2, overflow: 'hidden',
                border: '1px solid #e2e8f0', height: 'fit-content', position: 'sticky', top: 24,
                transition: 'width 0.3s ease',
            }}>
                {/* Header */}
                <Box sx={{ px: isCollapsed ? 1 : 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                    {!isCollapsed && <Typography variant="subtitle2" fontWeight={700} fontSize="0.85rem">Watchlist</Typography>}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {!isCollapsed && (
                            <Tooltip title="Add / Remove symbols">
                                <IconButton size="small" onClick={() => setAddDialogOpen(true)}
                                    sx={{ color: '#94a3b8', '&:hover': { bgcolor: 'rgba(245,158,11,0.1)', color: '#f59e0b' } }}>
                                    <AddIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={isCollapsed ? 'Expand watchlist' : 'Collapse watchlist'}>
                            <IconButton size="small" onClick={() => setIsCollapsed(!isCollapsed)}
                                sx={{ color: '#94a3b8', '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: '#3b82f6' } }}>
                                <MenuOpenIcon sx={{ fontSize: 18, transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {isCollapsed ? (
                    /* Collapsed: show symbol avatars only */
                    <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                        {symbols.map(s => {
                            const isActive = String(s.id) === String(currentSymbolId);
                            return (
                                <Tooltip key={s.id} title={`${s.symbol} — $${s.current_price ? Number(s.current_price).toFixed(2) : '-'}`} placement="left">
                                    <IconButton size="small" onClick={() => onSymbolClick(s.id)}
                                        sx={{ border: isActive ? '2px solid #22c55e' : '2px solid transparent', p: 0.25 }}>
                                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', fontWeight: 700, bgcolor: getSymbolColor(s.symbol) }}>
                                            {(s.symbol || '?')[0]}
                                        </Avatar>
                                    </IconButton>
                                </Tooltip>
                            );
                        })}
                    </Box>
                ) : (
                    <>
                        {/* Column Headers */}
                        <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1 }}>Symbol</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ width: 70, textAlign: 'right' }}>Last</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ width: 60, textAlign: 'right' }}>Chg</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ width: 60, textAlign: 'right' }}>Chg%</Typography>
                        </Box>

                        {loading ? (
                            <Box sx={{ p: 2 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.5, borderRadius: 1 }} />)}</Box>
                        ) : symbols.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No symbols in watchlist</Typography></Box>
                        ) : (
                            <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                                {Object.entries(grouped).map(([type, items]) => (
                                    <Box key={type}>
                                        <Box sx={{ px: 2, py: 0.5, bgcolor: '#f8fafc' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.7rem" textTransform="uppercase" letterSpacing={0.5}>
                                                {type}
                                            </Typography>
                                        </Box>
                                        {items.map((s) => {
                                            const change = s.current_price && s.previous_close ? (s.current_price - s.previous_close) : null;
                                            const changePct = s.change_percent;
                                            const isPositive = change != null ? change >= 0 : null;
                                            const color = isPositive === null ? '#94a3b8' : isPositive ? '#22c55e' : '#ef4444';
                                            const isActive = String(s.id) === String(currentSymbolId);

                                            return (
                                                <Box
                                                    key={s.id}
                                                    onClick={() => onSymbolClick(s.id)}
                                                    sx={{
                                                        px: 2, py: 1, display: 'flex', alignItems: 'center', cursor: 'pointer',
                                                        bgcolor: isActive ? '#f0fdf4' : 'transparent',
                                                        borderLeft: isActive ? '3px solid #22c55e' : '3px solid transparent',
                                                        '&:hover': { bgcolor: isActive ? '#f0fdf4' : '#f8fafc', '& .remove-btn': { opacity: 1 } },
                                                        transition: 'background-color 0.15s',
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                        <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem', fontWeight: 700, bgcolor: getSymbolColor(s.symbol) }}>
                                                            {(s.symbol || '?')[0]}
                                                        </Avatar>
                                                        <Typography variant="body2" fontWeight={600} fontSize="0.8rem" noWrap>{s.symbol}</Typography>
                                                        <DotIcon sx={{ fontSize: 6, color: '#22c55e' }} />
                                                    </Box>
                                                    <Typography variant="body2" fontWeight={500} fontSize="0.8rem" sx={{ width: 70, textAlign: 'right' }}>
                                                        {s.current_price ? Number(s.current_price).toFixed(2) : '-'}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500} fontSize="0.8rem" sx={{ width: 60, textAlign: 'right', color }}>
                                                        {change != null ? `${change.toFixed(2)}` : '-'}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem" sx={{ width: 60, textAlign: 'right', color }}>
                                                        {changePct != null ? `${changePct.toFixed(2)}%` : '-'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            {/* Add/Remove Symbol Dialog */}
            <Dialog open={addDialogOpen} onClose={() => { setAddDialogOpen(false); setSymbolSearch(''); setSearchResults([]); }} maxWidth="sm" fullWidth>
                <DialogTitle>Add / Remove Symbols</DialogTitle>
                <DialogContent>
                    {/* Search to add */}
                    <TextField
                        fullWidth size="small" placeholder="Search by ticker or company name..."
                        value={symbolSearch} onChange={(e) => setSymbolSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSymbolSearch(); }}
                        sx={{ mt: 1, mb: 2 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: <InputAdornment position="end">{searching ? <CircularProgress size={18} /> : <Button size="small" onClick={handleSymbolSearch}>Search</Button>}</InputAdornment>,
                        }}
                    />
                    {searchResults.length > 0 && (
                        <List dense sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                            {searchResults.map((result, i) => (
                                <ListItem key={i} sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <ListItemText
                                        primary={<Typography fontWeight="bold">{result.symbol || result.ticker}</Typography>}
                                        secondary={result.name || result.company_name}
                                    />
                                    <ListItemSecondaryAction>
                                        <Button size="small" variant="outlined"
                                            startIcon={addingSymbol === (result.symbol || result.ticker) ? <CircularProgress size={14} /> : <AddIcon />}
                                            onClick={() => handleAddSymbol(result)}
                                            disabled={addingSymbol === (result.symbol || result.ticker)}
                                            sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' } }}>
                                            Add
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {/* Current symbols with remove option */}
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Current Watchlist</Typography>
                    <List dense sx={{ maxHeight: 250, overflow: 'auto' }}>
                        {symbols.map(s => (
                            <ListItem key={s.id} sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', fontWeight: 700, bgcolor: getSymbolColor(s.symbol), mr: 1.5 }}>
                                    {(s.symbol || '?')[0]}
                                </Avatar>
                                <ListItemText
                                    primary={<Typography fontWeight={600} fontSize="0.85rem">{s.symbol}</Typography>}
                                    secondary={s.company_name}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton size="small" onClick={(e) => handleRemoveSymbol(e, s.id)}
                                        disabled={deletingSymbol === s.id}
                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>
                                        {deletingSymbol === s.id ? <CircularProgress size={16} /> : <DeleteIcon sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAddDialogOpen(false); setSymbolSearch(''); setSearchResults([]); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const SymbolDetailPage = () => {
    const { symbolId } = useParams();
    const navigate = useNavigate();
    const [canView, setCanView] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [symbolData, setSymbolData] = useState(null);
    const [tabData, setTabData] = useState({});
    const [tabLoading, setTabLoading] = useState({});
    const [tabError, setTabError] = useState({});
    const [watchlistSymbols, setWatchlistSymbols] = useState([]);
    const [watchlistLoading, setWatchlistLoading] = useState(true);

    // Reset all state when symbolId changes (e.g. clicking a different symbol in watchlist)
    useEffect(() => {
        setSymbolData(null);
        setTabData({});
        setTabLoading({});
        setTabError({});
        setActiveTab(0);
    }, [symbolId]);

    // Permission check
    useEffect(() => {
        const check = async () => {
            const view = await hasPermission('tr-watchlist', 'view');
            setCanView(view);
        };
        check();
    }, []);

    // Fetch watchlist symbols for side panel
    const fetchWatchlist = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/stock-trading/symbols?per_page=50&sort_by=symbol&sort_order=asc');
            if (res.data.success) setWatchlistSymbols(res.data.data || []);
        } catch (e) { /* ignore */ }
        finally { setWatchlistLoading(false); }
    }, []);

    useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

    const handleWatchlistSymbolClick = (id) => {
        navigate(`/trading-portal/symbol-detail/${id}`);
    };

    // Fetch tab data (lazy, only when tab is selected)
    const fetchTabData = useCallback(async (tabIndex) => {
        if (tabData[tabIndex] || tabLoading[tabIndex]) return;
        setTabLoading(prev => ({ ...prev, [tabIndex]: true }));
        setTabError(prev => ({ ...prev, [tabIndex]: null }));
        try {
            const endpoint = TAB_CONFIG[tabIndex].endpoint;
            const response = await axiosInstance.get(`/api/stock-trading/symbol-detail/${symbolId}/${endpoint}`);
            if (response.data.success) {
                setTabData(prev => ({ ...prev, [tabIndex]: response.data.data }));
                if (tabIndex === 0 && response.data.data.symbol) {
                    setSymbolData(response.data.data.symbol);
                }
            } else {
                setTabError(prev => ({ ...prev, [tabIndex]: response.data.message }));
            }
        } catch (err) {
            setTabError(prev => ({ ...prev, [tabIndex]: err.response?.data?.message || 'Failed to load data' }));
        } finally {
            setTabLoading(prev => ({ ...prev, [tabIndex]: false }));
        }
    }, [symbolId, tabData, tabLoading]);

    // Fetch overview on mount
    useEffect(() => {
        if (canView) fetchTabData(0);
    }, [canView, fetchTabData]);

    // Fetch when tab changes
    useEffect(() => {
        if (canView) fetchTabData(activeTab);
    }, [activeTab, canView, fetchTabData]);

    // Refresh current tab
    const handleRefresh = () => {
        setTabData(prev => { const copy = { ...prev }; delete copy[activeTab]; return copy; });
    };

    const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view this page.</Alert>
            </Box>
        );
    }

    // Active tab component
    const ActiveComponent = TAB_CONFIG[activeTab].Component;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate('/trading-portal/watchlist')}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                        <Typography variant="h4" fontWeight="bold">
                            {symbolData?.symbol || 'Loading...'}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            {symbolData?.company_name || ''}
                        </Typography>
                    </Box>
                    {symbolData && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Typography variant="h5" fontWeight="bold">
                                {formatPrice(symbolData.current_price)}
                            </Typography>
                            {symbolData.current_price && symbolData.previous_close && (() => {
                                const change = symbolData.current_price - symbolData.previous_close;
                                const changePct = symbolData.previous_close ? (change / symbolData.previous_close * 100) : 0;
                                const isPositive = change >= 0;
                                return (
                                    <Typography variant="h6" sx={{ color: isPositive ? '#22c55e' : '#ef4444' }}>
                                        {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePct.toFixed(2)}%)
                                    </Typography>
                                );
                            })()}
                            {symbolData.last_price_update && (
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    Last updated: {new Date(symbolData.last_price_update).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                            )}
                            <Tooltip title="Refresh current tab">
                                <IconButton size="small" onClick={handleRefresh}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Main Content + Watchlist Panel */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                {/* Left: Tabs + Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Tabs */}
                    <Paper sx={{ mb: 3 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, v) => setActiveTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            indicatorColor="primary"
                            textColor="primary"
                            sx={{ '& .MuiTab-root': { textTransform: 'none', minHeight: 48 } }}
                        >
                            {TAB_CONFIG.map((tab, i) => (
                                <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
                            ))}
                        </Tabs>
                    </Paper>

                    {/* Active Tab Content */}
                    <ActiveComponent
                        data={tabData[activeTab]}
                        loading={tabLoading[activeTab]}
                        error={tabError[activeTab]}
                        symbolId={symbolId}
                        symbolData={symbolData}
                    />
                </Box>

                {/* Right: Watchlist Panel */}
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <WatchlistPanel
                        symbols={watchlistSymbols}
                        loading={watchlistLoading}
                        currentSymbolId={symbolId}
                        onSymbolClick={handleWatchlistSymbolClick}
                        onRefresh={fetchWatchlist}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default SymbolDetailPage;
